import React, { useEffect, useMemo, useState } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar as CalIcon, MapPin, Trophy, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const CAT_COLOR = {
  "Sub-12": "hsl(199 90% 60%)", "Sub-14": "hsl(265 85% 65%)",
  "Sub-16": "hsl(82 90% 55%)", "Sub-20": "hsl(45 100% 60%)",
  "Adulto": "hsl(0 70% 60%)",
};
const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function MyCalendar() {
  const [games, setGames] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/games");
        setGames(data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const monthGames = useMemo(() => {
    const prefix = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
    return games.filter(g => (g.date || "").startsWith(prefix));
  }, [games, cursor]);

  const gamesByDay = useMemo(() => {
    const map = {};
    for (const g of monthGames) {
      const d = parseInt((g.date || "").slice(8, 10), 10);
      if (!map[d]) map[d] = [];
      map[d].push(g);
    }
    return map;
  }, [monthGames]);

  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const firstWeekday = new Date(cursor.y, cursor.m, 1).getDay();
  const cells = Array.from({ length: firstWeekday + daysInMonth }, (_, i) =>
    i < firstWeekday ? null : i - firstWeekday + 1
  );

  const navMonth = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  return (
    <div data-testid="student-calendar-page">
      <PageHeader eyebrow="Jogos" title="Meu calendário" subtitle="Veja os jogos da sua categoria e confira suas convocações." mascot={BRAND.MANGUITA} />

      <div className="vv-card p-4 mb-4 flex items-center justify-between gap-2">
        <Button variant="ghost" size="icon" onClick={() => navMonth(-1)} data-testid="prev-month"><ChevronLeft /></Button>
        <div className="text-xl font-black capitalize">{MONTHS_PT[cursor.m]}/{cursor.y}</div>
        <Button variant="ghost" size="icon" onClick={() => navMonth(1)} data-testid="next-month"><ChevronRight /></Button>
      </div>

      <div className="vv-card p-2 sm:p-4 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[560px]">
          {WEEKDAYS.map(w => <div key={w} className="text-center text-[10px] uppercase tracking-widest text-muted-foreground py-1">{w}</div>)}
          {cells.map((d, i) => (
            <div key={i} className={`min-h-[70px] rounded border p-1 ${d ? "border-border/60 bg-background/40" : "border-transparent"}`}>
              {d && (
                <>
                  <div className="text-xs font-bold text-muted-foreground">{d}</div>
                  <div className="space-y-1 mt-1">
                    {(gamesByDay[d] || []).map(g => (
                      <div
                        key={g.id}
                        className={`text-[10px] px-1 py-0.5 rounded truncate font-bold relative ${g.called ? "ring-2 ring-[hsl(var(--vv-yellow))]" : ""}`}
                        style={{ background: `${CAT_COLOR[g.category]}30`, color: CAT_COLOR[g.category] }}
                        title={g.competition}
                      >
                        {g.called && <Sparkles className="size-3 absolute -top-1 -right-1 text-[hsl(var(--vv-yellow))]" />}
                        {g.time}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {monthGames.length === 0 ? (
        <div className="mt-4"><EmptyState title="Nenhum jogo este mês" description="Fique de olho — assim que surgir um jogo da sua categoria aparecerá aqui." mascot={BRAND.MANGUITO} /></div>
      ) : (
        <div className="mt-6 space-y-3">
          {monthGames.map(g => (
            <div key={g.id} className={`vv-card p-4 ${g.called ? "border-[hsl(var(--vv-yellow))] shadow-[0_0_0_1px_hsl(var(--vv-yellow)/0.3)]" : ""}`} data-testid={`student-game-${g.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="vv-chip font-bold whitespace-nowrap" style={{ background: `${CAT_COLOR[g.category]}30`, color: CAT_COLOR[g.category] }}>
                      {g.category} / {g.gender === "F" ? "Fem." : "Masc."}
                    </span>
                    <span className="font-black">{g.competition}</span>
                    {g.called && (
                      <span className="vv-chip vv-chip-yellow font-black uppercase tracking-wider" data-testid={`called-badge-${g.id}`}>
                        <Trophy className="size-3 mr-1 inline" /> Convocado
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-1"><CalIcon className="size-3.5" />{g.date} • {g.time}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="size-3.5" />{g.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
